import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { cancelationSubscribe } from 'app/services/api/utils';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import { entityAdapter } from './adapter';
import { ManageWithdrawState } from './interfaces';
import { PaginatedResponse } from 'app/interfaces/api';
import { Pagination } from 'app/interfaces';
import { store } from 'redux/store';
import { DEFAULT_PAGINATION_SETTINGS } from 'app/constants';
import { DEFAULT_LIMIT } from './constants';
import { WithdrawRequestEntity } from 'app/interfaces/entity';
import { ManageWithdrawStoreStatus } from './enums';

export const getWithdrawList = createAsyncThunk(
  `${EntityStoreName.ManageWithdraw}/getWithdrawRequests`,
  (_, { signal }): Promise<PaginatedResponse<WithdrawRequestEntity[]>> => {
    const cancelToken = cancelationSubscribe(signal);
    const { limit = DEFAULT_LIMIT, offset = 1 } = store.getState()[
      EntityStoreName.ManageWithdraw
    ] ?? {
      ...DEFAULT_PAGINATION_SETTINGS,
      limit: DEFAULT_LIMIT,
    };

    return apiRequestCall<PaginatedResponse<WithdrawRequestEntity[]>>({
      params: {
        method: 'GET',
        resource: ApiResource.Admin,
        urlPrefix: 'payments/manual-requests',
        params: {
          limit,
          offset: (offset - 1) * limit,
        },
        cancelToken,
      },
    });
  }
);

export const updateWithdrawRequest = createAsyncThunk(
  `${EntityStoreName.ManageWithdraw}/updateWithdrawRequest`,
  (
    { id, status, note }: Pick<WithdrawRequestEntity, 'id' | 'status' | 'note'>,
    { signal }
  ): Promise<WithdrawRequestEntity> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<WithdrawRequestEntity>({
      params: {
        method: 'PATCH',
        resource: ApiResource.Admin,
        urlPrefix: `payments/manual-requests/${id}/status`,
        cancelToken,
        data: { status, note },
      },
    });
  }
);

export const updatePaginationReducer = (
  state: ManageWithdrawState,
  action: PayloadAction<{ data: Partial<Pagination> }>
) => {
  return {
    ...state,
    limit: action.payload.data.limit ?? state.limit,
    offset: action.payload.data.offset ?? state.offset,
  };
};

export const registerReducers = (builder: ActionReducerMapBuilder<ManageWithdrawState>): void => {
  builder
    .addCase(getWithdrawList.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        ManageWithdrawStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getWithdrawList.fulfilled, (state, action) => {
      entityAdapter.setAll(state, action.payload.data);
      state.total = action.payload.total;

      entityStoreStatus.removeRequestId(
        state,
        ManageWithdrawStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getWithdrawList.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        ManageWithdrawStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(updateWithdrawRequest.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        ManageWithdrawStoreStatus.UpdatingOne,
        action.meta.requestId
      );
    })
    .addCase(updateWithdrawRequest.fulfilled, (state, action) => {
      entityAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
      entityStoreStatus.removeRequestId(
        state,
        ManageWithdrawStoreStatus.UpdatingOne,
        action.meta.requestId
      );
    })
    .addCase(updateWithdrawRequest.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        ManageWithdrawStoreStatus.UpdatingOne,
        action.meta.requestId
      );
    });
};
